import { isApiError } from '@care-platform/api-client';
import { ErrorState, LoadingState, NotAvailableState } from '@care-platform/ui';
import type { ReactNode } from 'react';
import { useCopy } from './copy';

/** The parts of a TanStack Query result this view reads. */
export interface QueryLike<T> {
  data: T | undefined;
  error: Error | null;
  isPending: boolean;
  refetch: () => unknown;
}

/** Message for a failed request, without leaking anything but the support reference. */
export function useErrorDescription(): (error: Error) => string {
  const copy = useCopy();
  return (error) => {
    if (!isApiError(error)) return copy.common.networkError;
    const reference = error.problem?.traceId ?? error.correlationId;
    return reference
      ? `${copy.common.errorBody} ${copy.common.errorReference(reference)}`
      : copy.common.errorBody;
  };
}

/**
 * Renders a query's loading, failure and success states the same way everywhere. An operation the API answers
 * 501 for (in the contract, not built yet) shows a calm "not available yet" panel rather than an error.
 */
export function QueryView<T>({
  query,
  notAvailable,
  notFound,
  loadingLabel,
  children,
}: {
  query: QueryLike<T>;
  /** What to say when the capability is not built yet. */
  notAvailable?: string;
  /** What to show when the API answers 404. */
  notFound?: ReactNode;
  loadingLabel?: string;
  children: (data: T) => ReactNode;
}) {
  const copy = useCopy();
  const describe = useErrorDescription();

  if (query.data !== undefined) return <>{children(query.data)}</>;
  if (query.error) {
    if (isApiError(query.error) && query.error.notImplemented) {
      return (
        <NotAvailableState
          title={copy.common.notAvailableTitle}
          description={notAvailable ?? copy.common.notAvailableBody}
        />
      );
    }
    if (isApiError(query.error) && query.error.notFound && notFound) return <>{notFound}</>;
    return (
      <ErrorState
        title={copy.common.errorTitle}
        description={describe(query.error)}
        retryLabel={copy.common.retry}
        onRetry={() => void query.refetch()}
      />
    );
  }
  return <LoadingState label={loadingLabel ?? copy.common.loading} />;
}
